import { useEffect, useState } from "react";
import { Icon } from "./Icon";
import type { Theme } from "../hooks/useTheme";
import { useI18n } from "../i18n/LanguageContext";
import { profile } from "../data/profile";
import styles from "./Nav.module.css";

interface NavProps {
  theme: Theme;
  onToggleTheme: () => void;
}

export function Nav({ theme, onToggleTheme }: NavProps) {
  const { lang, toggle: toggleLang, t, ui } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const name = t(profile.name);
  const monogram = name
    .split(/\s+/)
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const links = [
    { href: "#expertise", label: ui.nav.expertise },
    { href: "#work", label: ui.nav.work },
    { href: "#education", label: ui.nav.education },
    { href: "#contact", label: ui.nav.contact },
  ];

  return (
    <header
      className={`${styles.bar} ${scrolled ? styles.scrolled : ""}`}
      data-open={menuOpen}
    >
      <div className={`container ${styles.inner}`}>
        <a href="#home" className={styles.brand} onClick={() => setMenuOpen(false)}>
          <span className={styles.mark}>{monogram}</span>
          <span className={styles.brandName}>{name}</span>
        </a>

        <nav className={styles.links} aria-label={ui.a11y.primaryNav}>
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className={styles.link}
              onClick={() => setMenuOpen(false)}
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className={styles.actions}>
          <button
            type="button"
            className={`${styles.iconBtn} ${styles.langBtn}`}
            onClick={toggleLang}
            aria-label={ui.a11y.switchLanguage}
          >
            {lang.toUpperCase()}
          </button>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={onToggleTheme}
            aria-label={theme === "dark" ? ui.a11y.switchToLight : ui.a11y.switchToDark}
          >
            <Icon name={theme === "dark" ? "sun" : "moon"} />
          </button>
          <button
            type="button"
            className={`${styles.iconBtn} ${styles.menuBtn}`}
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-label={ui.a11y.toggleMenu}
          >
            <Icon name={menuOpen ? "close" : "menu"} />
          </button>
        </div>
      </div>
    </header>
  );
}
